"use client";

import React, { createContext, useContext, useState, useEffect } from "react";

export type TopOfferItem = { emoji?: string; bold: string; body: string; code?: string | null };

interface TenantInfo {
  storeName: string;
  logoUrl?: string | null;
  outletId: string;
  tenantId?: string;
  prefix: string;
  /** Store free-delivery threshold + fulfillment city — surfaced in the header utility strip. */
  minCartValue?: number | null;
  city?: string | null;
  /** When topOfferBanner is enabled with offers, drives the header ticker. */
  topOfferBannerOffers?: TopOfferItem[];
}

interface TenantContextValue extends TenantInfo {
  setTenant: (info: TenantInfo) => void;
  setTopOfferBannerOffers: (offers: TopOfferItem[] | undefined) => void;
}

const STORAGE_KEY = "shop_tenant";

const TenantContext = createContext<TenantContextValue | null>(null);

export function TenantProvider({ children }: { children: React.ReactNode }) {
  const [info, setInfo] = useState<TenantInfo>({
    // Neutral until the tenant's real brand loads — a seller's storefront must
    // never flash the platform name ("GrabLocal") in its own header.
    storeName: "",
    logoUrl: null,
    outletId: "",
    prefix: "",
    minCartValue: null,
    city: null,
  });

  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) setInfo(prev => ({ ...prev, ...JSON.parse(stored) }));
    } catch { /* ignore */ }
  }, []);

  const setTenant = (next: TenantInfo) => {
    setInfo(prev => ({ ...prev, ...next }));
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  };

  const setTopOfferBannerOffers = (offers: TopOfferItem[] | undefined) => {
    setInfo(prev => ({ ...prev, topOfferBannerOffers: offers }));
  };

  return (
    <TenantContext.Provider value={{ ...info, setTenant, setTopOfferBannerOffers }}>
      {children}
    </TenantContext.Provider>
  );
}

export function useTenant() {
  const ctx = useContext(TenantContext);
  if (!ctx) throw new Error("useTenant must be used within TenantProvider");
  return ctx;
}
